import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ErrorOutline,
  EventNoteOutlined,
  GppMaybeOutlined,
  HourglassTopOutlined,
  NotificationsNoneOutlined,
  NotificationsOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  SupportAgentOutlined,
  TaskAltOutlined,
  VerifiedUserOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  AppNotification,
  NotificationType,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../Services/notificationsApi";
import { timeAgo } from "../../Utils/timeAgo";

const TEAL = "#004B4C";
// Check for new alerts this often while the tab is visible.
export const BELL_POLL_MS = 2 * 60 * 1000;

const ICONS: Record<NotificationType, { icon: React.ElementType; color: string; bg: string }> = {
  claim_submitted: { icon: HourglassTopOutlined, color: "#b26a00", bg: "rgba(251,190,104,0.22)" },
  claim_approved: { icon: VerifiedUserOutlined, color: "#2e7d32", bg: "rgba(46,125,50,0.10)" },
  claim_rejected: { icon: ErrorOutline, color: "#c62828", bg: "rgba(198,40,40,0.08)" },
  ownership_transferred: { icon: GppMaybeOutlined, color: "#c62828", bg: "rgba(198,40,40,0.08)" },
  ownership_removed: { icon: GppMaybeOutlined, color: "#b26a00", bg: "rgba(251,190,104,0.22)" },
  vendor_record: { icon: ReceiptLongOutlined, color: TEAL, bg: "rgba(0,75,76,0.10)" },
  bid_received: { icon: RequestQuoteOutlined, color: TEAL, bg: "rgba(0,75,76,0.10)" },
  service_request_update: { icon: SupportAgentOutlined, color: TEAL, bg: "rgba(0,75,76,0.10)" },
  job_completed: { icon: TaskAltOutlined, color: "#2e7d32", bg: "rgba(46,125,50,0.10)" },
  maintenance_due: { icon: EventNoteOutlined, color: TEAL, bg: "rgba(0,75,76,0.10)" },
  maintenance_overdue: { icon: WarningAmberOutlined, color: "#c62828", bg: "rgba(198,40,40,0.08)" },
};

// Shared with the local-news card; re-exported for existing imports.
export { timeAgo };

/**
 * The header bell (October 2026; Asher wanted on-site alerts, not just email):
 * claim updates, vendor records and bids, finished jobs, and maintenance
 * that's coming due. Signed-in users only — the header decides that.
 */
const NotificationBell: React.FC<{ size?: "small" | "medium" }> = ({ size = "medium" }) => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const alive = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { notifications, unreadCount } = await getNotifications();
      if (!alive.current) return;
      setItems(notifications);
      setUnread(unreadCount);
      setError(false);
    } catch {
      if (alive.current) setError(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, BELL_POLL_MS);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [load]);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    load();
  };

  const openItem = (n: AppNotification) => {
    if (!n.readAt) {
      setItems((prev) => prev?.map((x) => (x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x)) || prev);
      setUnread((u) => Math.max(0, u - 1));
      markNotificationRead(n._id).catch(() => {
        /* the next poll corrects the count */
      });
    }
    setAnchor(null);
    if (n.link) navigate(n.link);
  };

  const readAll = () => {
    const stamp = new Date().toISOString();
    setItems((prev) => prev?.map((x) => (x.readAt ? x : { ...x, readAt: stamp })) || prev);
    setUnread(0);
    markAllNotificationsRead().catch(() => load());
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={open}
          aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
          aria-haspopup="dialog"
          sx={{ p: size === "small" ? 1 : 0 }}
          data-testid="notification-bell"
        >
          <Badge
            badgeContent={unread}
            max={9}
            color="error"
            overlap="circular"
            data-testid="notification-badge"
          >
            {unread ? <NotificationsOutlined color="action" /> : <NotificationsNoneOutlined color="action" />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { mt: 1, width: { xs: "calc(100vw - 32px)", sm: 380 }, maxWidth: 380, borderRadius: "14px" },
          role: "dialog",
          "aria-label": "Notifications",
        } as any}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 2, borderBottom: "1px solid rgba(0,75,76,0.12)" }}
        >
          <Typography sx={{ fontWeight: 800, color: TEAL }}>Notifications</Typography>
          <Button
            size="small"
            onClick={readAll}
            disabled={!unread}
            sx={{ color: TEAL, fontWeight: 700 }}
            data-testid="notifications-read-all"
          >
            Mark all as read
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
          {items === null && loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={22} sx={{ color: TEAL }} />
            </Stack>
          ) : error && !items?.length ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Couldn't load your notifications.
              </Typography>
              <Button size="small" onClick={load} sx={{ color: TEAL, fontWeight: 700 }}>
                Try again
              </Button>
            </Stack>
          ) : !items?.length ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 7, px: 3, textAlign: "center" }} data-testid="notifications-empty">
              <NotificationsNoneOutlined sx={{ color: TEAL, fontSize: 32, opacity: 0.7 }} />
              <Typography sx={{ fontWeight: 700 }}>You're all caught up</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Claim updates, service requests and maintenance reminders will show up here.
              </Typography>
            </Stack>
          ) : (
            items.map((n) => {
              const style = ICONS[n.type] || ICONS.vendor_record;
              const Icon = style.icon;
              const isUnread = !n.readAt;
              return (
                <ButtonBase
                  key={n._id}
                  onClick={() => openItem(n)}
                  sx={{
                    width: "100%",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    gap: 2.5,
                    px: 3,
                    py: 2.5,
                    bgcolor: isUnread ? "rgba(0,75,76,0.04)" : "transparent",
                    borderBottom: "1px solid rgba(0,75,76,0.08)",
                    "&:hover": { bgcolor: "rgba(0,75,76,0.07)" },
                  }}
                  data-testid={`notification-${n._id}`}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      borderRadius: "10px",
                      bgcolor: style.bg,
                      color: style.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 19 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: isUnread ? 800 : 600, fontSize: 14, color: "#1d2b2b", lineHeight: 1.35 }}>
                      {n.title}
                    </Typography>
                    {n.body && (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: "text.secondary",
                          mt: 0.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {n.body}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>{timeAgo(n.createdAt)}</Typography>
                  </Box>
                  {isUnread && (
                    <Box
                      aria-label="Unread"
                      sx={{ width: 8, height: 8, mt: 1, flexShrink: 0, borderRadius: "50%", bgcolor: "#c62828" }}
                    />
                  )}
                </ButtonBase>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
